import React, { useState } from 'react';

interface Language {
  code: string;
  name: string;
  short: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', short: 'EN' },
  { code: 'fa', name: 'فارسی', short: 'FA' },
  { code: 'ar', name: 'العربية', short: 'AR' },
  { code: 'fr', name: 'Français', short: 'FR' },
  { code: 'es', name: 'Español', short: 'ES' },
  { code: 'zh', name: '中文', short: 'ZH' },
];

interface SimpleLanguageSelectorProps {
  variant?: 'sidebar' | 'header';
}

function getCurrentLangFromCookie(): Language {
  const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
  if (match) {
    const code = match[1] === 'zh-CN' ? 'zh' : match[1];
    return languages.find(l => l.code === code) || languages[0];
  }
  return languages[0];
}

const SimpleLanguageSelector: React.FC<SimpleLanguageSelectorProps> = ({ variant = 'sidebar' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState<Language>(() => getCurrentLangFromCookie());

  const handleSelect = (lang: Language) => {
    setCurrentLang(lang);
    setIsOpen(false);

    const googleLangMap: Record<string, string> = {
      en: 'en', fa: 'fa', ar: 'ar', fr: 'fr', es: 'es', zh: 'zh-CN',
    };
    const googleLang = googleLangMap[lang.code] || lang.code;

    if (lang.code === 'en') {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
      window.location.reload();
      return;
    }

    const select = document.querySelector('.goog-te-combo') as HTMLSelectElement;
    if (select) {
      select.value = googleLang;
      select.dispatchEvent(new Event('change'));
    } else {
      document.cookie = `googtrans=/en/${googleLang}`;
      document.cookie = `googtrans=/en/${googleLang}; domain=${window.location.hostname}`;
      window.location.reload();
    }
  };

  // ===== HEADER variant - کوچیک، فقط EN =====
  if (variant === 'header') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2.5 py-1 rounded text-white text-xs font-semibold border border-white/30 hover:bg-white/20 transition-colors"
        >
          <span>{currentLang.short}</span>
          <span className="text-[9px] opacity-70">{isOpen ? '▲' : '▼'}</span>
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] overflow-hidden">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleSelect(lang)}
                  className={`flex items-center justify-between w-full px-3 py-2 border-none text-xs text-left cursor-pointer transition-colors
                    ${currentLang.code === lang.code ? 'bg-blue-50 text-blue-700 font-semibold' : 'bg-white text-gray-800 hover:bg-gray-50'}`}
                >
                  <span>{lang.name}</span>
                  {currentLang.code === lang.code && <span className="text-blue-600">✓</span>}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // ===== SIDEBAR variant - قدیمی با اسم کامل =====
  return (
    <div className="relative w-full">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-white/15 border border-white/25 rounded-lg text-white text-sm cursor-pointer hover:bg-white/25 transition-colors"
      >
        <span className="font-medium">{currentLang.name}</span>
        <span className="text-xs opacity-70">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
          <div className="absolute bottom-full left-0 mb-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] overflow-hidden">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang)}
                className={`flex items-center gap-3 w-full px-4 py-3 border-none text-sm text-left cursor-pointer transition-colors
                  ${currentLang.code === lang.code ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-800 hover:bg-gray-50'}`}
              >
                <span>{lang.name}</span>
                {currentLang.code === lang.code && (
                  <span className="ml-auto text-blue-600 font-bold">✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SimpleLanguageSelector;
